
import Vue from 'vue';
import { IStatistic } from '../../store/advertiser-insights';
import QueryParamButtonGroup from '~shared/components/selectors/QueryParamButtonGroup.vue';
import CampaignLevelTreeSelector from '../../components/advertiser/CampaignLevelTreeSelector.vue';
import { isStrosslerEmail } from '~shared/utils';
import TableSettingsDropdown from '../../../marketplace/components/generic/TableSettingsDropdown.vue';

export default Vue.extend({
  components: {
    QueryParamButtonGroup,
    CampaignLevelTreeSelector,
    TableSettingsDropdown,
  },
  props: {
    creativeAdId: {
      type: String,
      default: ''
    },
    groupBy: {
      type: String,
      required: true,
    },
    includeArchivedCampaigns: {
      type: Boolean,
      default: false,
    },
    isNoData: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputGroupBy: '',
      defaultTableSettingsState: [
        'campaignName',
        'adImage',
        'adTitle',
        'date',
        'adSetName',
        'clicks',
        'impressions',
        'ctr',
        'spent',
        'ecpm',
        'averageTimespent',
        'conversions',
        'costPerVisits',
        'landingRate',
        'costPerAction',
        'conversionRate',
      ],
      tableSettingsOptions: [
        { item: 'clicks', name: 'Clicks' },
        { item: 'impressions', name: 'Impressions' },
        { item: 'ctr', name: 'CTR' },
        { item: 'spent', name: 'Spent' },

        { item: 'ecpm', name: 'eCPM' },
        { item: 'averageTimespent', name: 'Engagement' },
        { item: 'conversions', name: 'Conversions' },
        { item: 'costPerVisits', name: 'Cost Per Visits' },
        { item: 'landingRate', name: 'Landing Rate %' },
        { item: 'costPerAction', name: 'Cost Per Action' },
        { item: 'conversionRate', name: 'Conversion Rate %' },
      ],
      tableName: 'overViewReport',
      campaignTableColumns: [],
      landingPageDefaultCols: [
        'averageTimespent',
        'conversions',
        'costPerVisits',
        'landingRate',
        'costPerAction',
        'conversionRate',
      ],
      nonStrossleReportSettingsKeys: [
        'averageTimespent',
        'conversions',
        'costPerVisits',
        'landingRate',
        'costPerAction',
        'conversionRate',
        'ecpm',
      ],
    };
  },
  computed: {
    fields(): any {
      // Define columns that may be used
      const columns = {
        campaignName: {
          key: 'campaignName',
          label: 'Campaign',
          sortable: true,
          stickyColumn: true,
        },
        adImage: {
          key: 'adImage',
          label: 'Ad',
        },
        adTitle: {
          key: 'adTitle',
          label: 'Title',
          sortable: true,
          stickyColumn: true,
        },
        date: {
          key: 'date',
          sortable: true,
          label: 'Day',
          stickyColumn: true,
        },
        adSetName: {
          key: 'adSetName',
          label: 'Ad set',
          sortable: true,
          stickyColumn: true,
        },
        clicks: {
          key: 'clicks',
          sortable: true,
        },
        impressions: {
          key: 'impressions',
          sortable: true,
        },
        ctr: {
          key: 'ctr',
          sortable: true,
          label: 'CTR %',
        },
        spent: {
          key: 'spent',
          sortable: true,
          label: 'Spent',
        },
        ecpm: {
          key: 'ecpm',
          sortable: true,
          label: 'eCPM',
        },
        averageTimespent: {
          key: 'averageTimespent',
          sortable: true,
          label: 'Engagement',
        },
        conversions: {
          key: 'conversions',
          sortable: true,
          label: 'Conversions',
        },
        costPerVisits: {
          key: 'costPerVisits',
          sortable: true,
          label: 'Cost Per Visit',
        },
        landingRate: {
          key: 'landingRate',
          sortable: true,
          label: 'Landing Rate %',
        },
        costPerAction: {
          key: 'costPerAction',
          sortable: true,
          label: 'Cost Per Action',
        },
        conversionRate: {
          key: 'conversionRate',
          sortable: true,
          label: 'Conversion Rate %',
        },
      };

      const fillFieldsArray = (fieldOrder, columns, resolution) => {
        return fieldOrder.reduce((acc, curr) => {
          if (curr === 'date' && (resolution || '').toUpperCase() === 'DAY') {
            acc.push(columns[curr]);
          }
          if (curr !== 'date') {
            acc.push(columns[curr]);
          }
          return acc;
        }, []);
      };

      const applyTableSettingsFilter = (tableFiledsArr) => {
        return tableFiledsArr.filter((item) => {
          return (this as any).campaignTableColumns.includes(item?.key);
        });
      };

      const reportSettings = Object.assign(
        {},
        this.$store.state['advertiser-insights']?.filters?.selectedGroup
          ?.reportSettings || {}
      );
      const isStrossleUser = isStrosslerEmail(
        this.$store.state.auth.user.email
      );
      const shouldShowEcpm = isStrossleUser || reportSettings.ecpm;
      if (isStrossleUser && reportSettings?.landingPageReportSettings) {
        reportSettings.landingPageReportSettings = this.landingPageDefaultCols;
      }

      switch (this.groupBy) {
        case 'ad': {
          const fieldOrder = [
            'adImage',
            'adTitle',
            'date',
            'adSetName',
            'campaignName',
            'clicks',
            'impressions',
            'ctr',
            'spent',
          ];

          if (shouldShowEcpm) fieldOrder.push('ecpm');

          fieldOrder.push(...(reportSettings.landingPageReportSettings || []));

          return applyTableSettingsFilter(
            fillFieldsArray(fieldOrder, columns, this.selectedResolution)
          );
        }
        case 'adset': {
          const fieldOrder = [
            'adSetName',
            'date',
            'campaignName',
            'clicks',
            'impressions',
            'ctr',
            'spent',
          ];

          if (shouldShowEcpm) fieldOrder.push('ecpm');

          fieldOrder.push(...(reportSettings.landingPageReportSettings || []));

          return applyTableSettingsFilter(
            fillFieldsArray(fieldOrder, columns, this.selectedResolution)
          );
        }
        default: {
          const fieldOrder = [
            'campaignName',
            'date',
            'clicks',
            'impressions',
            'ctr',
            'spent',
          ];

          if (shouldShowEcpm) fieldOrder.push('ecpm');

          fieldOrder.push(...(reportSettings.landingPageReportSettings || []));

          return applyTableSettingsFilter(
            fillFieldsArray(fieldOrder, columns, this.selectedResolution)
          );
        }
      }
    },
    isLoading(): boolean {
      return this.$store.state['advertiser-insights']
        .isFetchingAdvertiserStatistics;
    },
    campaignId(): string | null {
      const selectedCampaign =
        this.$store.state['advertiser-insights'].filters.selectedCampaign;
      if (!selectedCampaign || !selectedCampaign.id) {
        return null;
      }
      return selectedCampaign.id;
    },
    currency(): string {
      return this.$store.state['advertiser-insights'].tableCurrency;
    },
    statistics(): IStatistic[] {
      if (this.selectedResolution.toUpperCase() === 'DAY') {
        return this.$store.state['advertiser-insights'].statistics
          .dailyGroupedBy;
      } else {
        return this.$store.state['advertiser-insights'].statistics
          .totalGroupedBy;
      }
    },
    selectedGroupBy(): string {
      return this.$store.state['advertiser-insights'].filters.selectedGroupBy;
    },
    selectedResolution(): string {
      return this.$store.state['advertiser-insights'].filters
        .selectedResolution;
    },
    landingPageReportSettings(): string[] {
      return this.$store.state['advertiser-insights']?.filters?.selectedGroup
        ?.reportSettings;
    },
  },
  watch: {
    selectedGroupBy: {
      handler(val): void {
        if (val === 'adset') {
          this.inputGroupBy = 'AD_SET';
        } else if (val === 'ad') {
          this.inputGroupBy = 'AD';
        } else {
          this.inputGroupBy = 'CAMPAIGN';
        }
      },
      immediate: true,
    },
    landingPageReportSettings: {
      immediate: true,
      handler(val): void {
        (this as any).removeUnavailableMetrics(val);
      },
    },
    statistics: {
      handler(val): void {
        this.$emit('update:isNoData', this.isLoading || !val.length);
      },
      deep: true,
      immediate: true,
    },
    isLoading: {
      handler(val): void {
        this.$emit('update:isNoData', val || !this.statistics.length);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    setGroupBy(groupBy): void {
      this.$store.dispatch('advertiser-insights/setSelectedGroupBy', groupBy);
    },
    setResolution(v): void {
      this.$store.commit('advertiser-insights/SET_SELECTED_RESOLUTION', v);
    },
    removeUnavailableMetrics(reportSettings) {
      const isStrossleUser = isStrosslerEmail(
        this.$store.state.auth.user.email
      );
      let landingPageReportSettings =
        reportSettings?.landingPageReportSettings || [];
      let isEcpm = reportSettings?.ecpm;

      if (!isStrossleUser) {
        // remove all landing page reporting metrics
        let newTableSettingsOptions: any = (
          this.tableSettingsOptions || []
        ).filter((item) => {
          return !this.nonStrossleReportSettingsKeys.includes(item?.item);
        });

        // find available landing page settings options
        let availableLandingReportSettOption: any =
          landingPageReportSettings.map((item) => {
            if (item === 'averageTimespent') {
              return { item: 'averageTimespent', name: 'Engagement' };
            }
            if (item === 'conversions') {
              return { item: 'conversions', name: 'Conversions' };
            }
            if (item === 'costPerVisits') {
              return { item: 'costPerVisits', name: 'Cost Per Visits' };
            }
            if (item === 'landingRate') {
              return { item: 'landingRate', name: 'Landing Rate %' };
            }
            if (item === 'costPerAction') {
              return { item: 'costPerAction', name: 'Cost Per Action' };
            }
            if (item === 'conversionRate') {
              return { item: 'conversionRate', name: 'Conversion Rate %' };
            }
          });

        // finally update the table settings options
        this.tableSettingsOptions = [
          ...newTableSettingsOptions,
          ...availableLandingReportSettOption,
        ];
        if (isEcpm) {
          this.tableSettingsOptions.push({ item: 'ecpm', name: 'eCPM' });
        }
      }
    },
  },
});
